import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

const GalleryPage: React.FC = () => {
  const { t } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<{ images: any[], index: number } | null>(null);

  const openModal = (images: any[], index: number) => {
    setSelectedImage({ images, index });
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  const goToPrevious = () => {
    if (selectedImage) {
      const newIndex = selectedImage.index === 0 ? selectedImage.images.length - 1 : selectedImage.index - 1;
      setSelectedImage({ ...selectedImage, index: newIndex });
    }
  };

  const goToNext = () => {
    if (selectedImage) {
      const newIndex = selectedImage.index === selectedImage.images.length - 1 ? 0 : selectedImage.index + 1;
      setSelectedImage({ ...selectedImage, index: newIndex });
    }
  };

  // Gallery Images
  const galleryImages = [
    { src: '/assets/carnival/WechatIMG11.jpg', alt: t('mainRoomAlt') },
    { src: '/assets/carnival/WechatIMG12.jpg', alt: t('grandBuffetAlt') },
    { src: '/assets/carnival/WechatIMG13.jpg', alt: t('liveKitchensAlt') },
    { src: '/assets/carnival/WechatIMG14.jpg', alt: t('dessertParadiseAlt') },
    { src: '/assets/carnival/WechatIMG15.jpg', alt: t('brazilianBbqAlt') },
    { src: '/assets/carnival/WechatIMG16.jpg', alt: t('sushiBarAlt') },
    { src: '/assets/carnival/WechatIMG17.jpg', alt: t('italianPastaCornerAlt') },
    { src: '/assets/carnival/WechatIMG18.jpg', alt: t('asianCuisineAlt') },
    { src: '/assets/carnival/WechatIMG21.jpg', alt: t('privateRoomsAlt') },
    { src: '/assets/carnival/WechatIMG22.jpg', alt: t('familySpaceAlt') },
    { src: '/assets/carnival/WechatIMG25.jpg', alt: t('seafoodAlt') },
    { src: '/assets/carnival/WechatIMG27.jpg', alt: t('chocolateFountainAlt') },
    { src: '/assets/carnival/WechatIMG28.jpg', alt: t('mexicanStationAlt') },
    { src: '/assets/carnival/WechatIMG29.jpg', alt: t('mediterraneanSectionAlt') },
    { src: '/assets/carnival/WechatIMG30.jpg', alt: t('kidsPlayAreaAlt') },
    { src: '/assets/carnival/WechatIMG31.jpg', alt: t('restaurantEntranceAlt') },
  ];

  return (
    <div className="bg-site-bg min-h-screen">
      {/* Hero Section */}
      <section className="pt-28 pb-12">
        <div className="section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-display mb-4 text-gray-800">
              {t('gallery')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('discoverCarnivalUniverseText')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-12">
        <div className="section-padding">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-display mb-8 text-gray-800 text-center"
          >
            {t('ourRestaurantTitle')}
          </motion.h2>
          
          {/* Gallery Masonry */}
          <div className="columns-2 md:columns-3 lg:columns-4 gap-6 space-y-6">
            {galleryImages.map((image, index) => {
              // Create varied heights for masonry effect
              const heights = ['h-48', 'h-64', 'h-56', 'h-72', 'h-60', 'h-80', 'h-52'];
              const randomHeight = heights[index % heights.length];
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ 
                    delay: index * 0.08,
                    duration: 0.5,
                    ease: "easeOut"
                  }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  className={`relative ${randomHeight} w-full overflow-hidden rounded-xl cursor-pointer group shadow-lg hover:shadow-2xl transition-all duration-300 mb-6 break-inside-avoid`}
                  onClick={() => openModal(galleryImages, index)}
                >
                <motion.img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  whileHover={{ scale: 1.15 }}
                  transition={{ duration: 0.4 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                
                {/* Zoom icon */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/90 p-2 rounded-full">
                    <ZoomIn size={20} className="text-carnival-charcoal" />
                  </div>
                </div>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Modal */}
      <AnimatePresence mode="wait">
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={closeModal}
          >
            {/* Close button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.1 }}
              className="absolute top-8 right-8 text-white/80 hover:text-white transition-colors z-50 bg-black/50 p-3 rounded-full"
              onClick={closeModal}
            >
              <X size={24} />
            </motion.button>

            {/* Previous button */}
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.1, x: -5 }}
              className="absolute left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-all bg-black/50 p-3 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
            >
              <ChevronLeft size={32} />
            </motion.button>

            {/* Next button */}
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.1, x: 5 }}
              className="absolute right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-all bg-black/50 p-3 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
            >
              <ChevronRight size={32} />
            </motion.button>

            {/* Image container */}
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedImage.index}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="max-w-7xl max-h-[90vh] px-4 flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage.images[selectedImage.index].src}
                  alt={selectedImage.images[selectedImage.index].alt}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                />
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-6"
                >
                  <p className="text-white text-center font-display text-xl">
                    {selectedImage.images[selectedImage.index].alt}
                  </p>
                  <p className="text-white/60 text-center mt-2">
                    {selectedImage.index + 1} / {selectedImage.images.length}
                  </p>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default GalleryPage;